import { z } from "zod";

import {
  UI_CONFIG_SCHEMA_VERSION,
  uiConfigOverlaySchema,
  type UiConfigOverlay,
} from "./document.js";

/**
 * Overlay document migration.
 *
 * Stored overlays are long-lived user data: every schema version that ever
 * shipped must migrate forward, losslessly where defined. Migrations run on
 * read (server-side before returning a document, client-side defensively on
 * the boot cache); writes always persist the current version.
 *
 * The corpus test (migrate.test.ts) pins one fixture per historical version
 * and asserts each migrates forward without throwing.
 */

const versionedDocumentSchema = z.looseObject({
  schemaVersion: z.number().int().positive(),
});

export type UiConfigMigrationResult =
  | { ok: true; document: UiConfigOverlay; migratedFrom: number }
  | {
      ok: false;
      reason: "unversioned" | "unknown-version" | "invalid-document";
      /** Present for unknown-version: a newer client wrote this document. */
      documentVersion?: number;
    };

/**
 * Step migrations keyed by source version; each lifts a document one
 * version. Version 1 is current, so the table is empty until v2 ships —
 * the shape exists so the corpus test enforces the discipline from day one.
 */
const stepMigrations: Record<
  number,
  (document: Record<string, unknown>) => Record<string, unknown>
> = {};

export function migrateUiConfigOverlay(
  input: unknown,
): UiConfigMigrationResult {
  const versioned = versionedDocumentSchema.safeParse(input);
  if (!versioned.success) {
    return { ok: false, reason: "unversioned" };
  }

  const sourceVersion = versioned.data.schemaVersion;
  if (sourceVersion > UI_CONFIG_SCHEMA_VERSION) {
    // Written by a newer client. Never destructively "migrate" downward —
    // callers should fall back to base config and preserve the stored doc.
    return {
      ok: false,
      reason: "unknown-version",
      documentVersion: sourceVersion,
    };
  }

  let document: Record<string, unknown> = versioned.data;
  for (let v = sourceVersion; v < UI_CONFIG_SCHEMA_VERSION; v += 1) {
    const step = stepMigrations[v];
    if (step === undefined) {
      return {
        ok: false,
        reason: "unknown-version",
        documentVersion: sourceVersion,
      };
    }
    document = { ...step(document), schemaVersion: v + 1 };
  }

  const parsed = uiConfigOverlaySchema.safeParse(document);
  if (!parsed.success) {
    return { ok: false, reason: "invalid-document" };
  }
  return { ok: true, document: parsed.data, migratedFrom: sourceVersion };
}
